/**
 * Event classification shared by the surfaces that gate festival-only features.
 *
 * Careful: there are two different "is this a festival" questions in this app
 * and they do not always agree.
 *
 *   - THIS one asks what the event IS, via events.event_type. It is the marker
 *     the pipeline sets and the feed RPCs filter on
 *     (`lower(event_type) <> 'festival' OR festival_ready`), and it is the one
 *     that decides whether a lineup exists.
 *   - eventCutoff.js asks where the event is HELD, via venues.venue_tier, to
 *     give festivals a longer join window. A club night at a festival-tier
 *     venue is a festival by that measure and not by this one.
 *
 * Use this module for anything lineup-related. Do not "unify" the two without
 * checking both call sites — they are asking different questions.
 */

/**
 * True when the event is a festival, and so can have a lineup.
 *
 * Prefers `is_festival`, which is a classification the pipeline owns. Falls
 * back to `event_type` for two reasons: rows the feed has not refreshed yet,
 * and any surface that queries `events` directly without selecting the new
 * column. The fallback is safe because event_type='festival' only ever appears
 * on the hand-curated festivals, which migration 098 backfilled as confirmed —
 * the two agree wherever both are present.
 *
 * Note event_type is NOT a classification: it is Ticketmaster's `type` field,
 * which is the literal string "event" on all 5,296 scraped rows. That is why
 * is_festival exists and why it wins here.
 */
export function isFestivalEvent(event) {
  if (typeof event?.is_festival === 'boolean') {
    return event.is_festival;
  }
  return String(event?.event_type || '').toLowerCase() === 'festival';
}

/**
 * False for a festival that is not yet festival_ready: the feed RPCs never return it and its event page says
 * "Event not found". The same predicate as theirs (`lower(event_type) <> 'festival' OR festival_ready`), so every
 * list of YOUR events agrees with the feed - an RSVP to a hidden festival never shows under Going, Past, Sets or
 * Groups (Sam, 2026-10-27: Camp Flog Gnaw showed as going while the events page could not show it).
 */
export function isEventListed(event) {
  if (!event) return false;
  return (
    String(event.event_type || '').toLowerCase() !== 'festival' || event.festival_ready === true
  );
}
